"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface EmailPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateType: 'rfq_digest' | 'custom_announcement';
  previewData?: {
    subject?: string;
    message?: string;
  };
}

export function EmailPreviewModal({ 
  open, 
  onOpenChange, 
  templateType,
  previewData 
}: EmailPreviewModalProps) {
  const renderRFQDigestPreview = () => (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <h3 className="font-semibold text-lg">📬 Daily RFQ Digest</h3>
        <p className="text-sm opacity-90 mt-1">New opportunities matching your profile</p>
      </div>
      
      <div className="space-y-3">
        <p className="text-sm">Hi <span className="font-medium">[Vendor Name]</span>,</p>
        <p className="text-sm text-muted-foreground">
          Here are <span className="font-medium">3 new RFQs</span> posted yesterday that match your capabilities:
        </p>
      </div>

      {/* Sample RFQ Cards */}
      {[1, 2, 3].map((i) => (
        <div key={i} className="rounded-lg border p-4 space-y-2 bg-muted/30">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium">Sample Job Title {i}</h4>
              <p className="text-sm text-muted-foreground">Sample Client Organization</p>
            </div>
            <span className="bg-primary/10 text-primary text-xs font-medium px-2 py-1 rounded">
              {95 - i * 5}% Match
            </span>
          </div>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span>RFQ Closes: Jan 15, 2025</span>
            <span>Due: Feb 28, 2025</span>
          </div>
          <div className="flex gap-1">
            <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">
              Printing
            </span>
            <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">
              Packaging
            </span>
          </div>
        </div>
      ))}

      <div className="text-center pt-4">
        <div className="inline-block bg-primary text-primary-foreground px-6 py-2 rounded-md text-sm font-medium">
          View All RFQs
        </div>
      </div>
    </div>
  );

  const renderAnnouncementPreview = () => (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <h3 className="font-semibold text-lg">📢 Announcement</h3>
        <p className="text-sm opacity-90 mt-1">
          {previewData?.subject || 'Your subject line here'}
        </p>
      </div>
      
      <div className="space-y-3">
        <p className="text-sm">Hi <span className="font-medium">[Vendor Name]</span>,</p>
        <div className="text-sm whitespace-pre-wrap text-muted-foreground">
          {previewData?.message || 'Your message content will appear here...'}
        </div>
      </div>

      <div className="text-center pt-4">
        <div className="inline-block bg-primary text-primary-foreground px-6 py-2 rounded-md text-sm font-medium">
          View RFQs
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Email Preview</DialogTitle>
          <DialogDescription>
            {templateType === 'rfq_digest' 
              ? 'Sample daily RFQ digest email'
              : 'Preview of your custom announcement'
            }
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="bg-background rounded-lg border p-6">
            {templateType === 'rfq_digest' 
              ? renderRFQDigestPreview()
              : renderAnnouncementPreview()
            }
            
            {/* Footer */}
            <div className="mt-8 pt-4 border-t text-center text-xs text-muted-foreground">
              <p>© 2025 Radius. All rights reserved.</p>
              <p className="mt-1">
                You're receiving this because you're a verified vendor on Radius.
              </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
